import { useAuth } from "@/features/auth/hooks/useAuth";
import { PageAccessError } from "@/lib/queries";

/**
 * Ops pages whose data is Access-unioned, not bound to the header tenant.
 * Switching the workspace must not page-deny when another Access org still
 * grants the PAGE (AD-2780: empty / out-of-header Access still loads the tracker).
 */
const CROSS_TENANT_PAGE_SLUGS = new Set(["marketplace.orders"]);

export function tenantHasPage(
  tenant: { permissions: Array<{ type: string; route: string }> },
  pageSlug: string,
): boolean {
  return tenant.permissions.some(
    (permission) => permission.type === "PAGE" && permission.route === pageSlug,
  );
}

/**
 * Synchronous guard for route beforeLoad hooks.
 * Throws PageAccessError immediately (no server round-trip) when the active
 * tenant's stored permissions don't include the requested page, preventing
 * stale-cache flashes of protected content.
 *
 * Falls through silently when activeTenantId is unset — the server check in
 * the loader is the final authority in that case.
 */
export function assertPagePermission(pageSlug: string): void {
  const { tenants, activeTenantId } = useAuth.getState();
  if (!activeTenantId) return;

  if (CROSS_TENANT_PAGE_SLUGS.has(pageSlug)) {
    if (tenants.some((tenant) => tenantHasPage(tenant, pageSlug))) return;
    if (tenants.length === 0) return;
    throw new PageAccessError(pageSlug);
  }

  const activeTenant = tenants.find((t) => t.id === activeTenantId);
  if (!activeTenant) return;

  if (!tenantHasPage(activeTenant, pageSlug)) throw new PageAccessError(pageSlug);
}
